import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { Modules } from '../enums/modules.enum';
import { UserService } from './user.service';
import { CompaniesService } from './companies.service';
import { RolesService } from '../permissions/roles.service';
import { EffectivePermissions } from '../permissions/models/role.models';
import { hasAction, PermissionAction } from '../permissions/permission-actions.enum';
import { LegacyPermission } from '../permissions/page-registry';

// Map a `LegacyPermission` to the localStorage cache key that holds its flag list. Lives at
// module scope so a guard can use this lookup without touching the service singleton — the
// service owns the cache writes, but the read shape is a stable contract.
const LEGACY_CACHE_KEY: Record<LegacyPermission, string> = {
  OnboardingAdmin:  'permissions',
  CorporateAdmin:   'corporatePermissions',
  InvoicingAdmin:   'invoicingPermissions',
  // Frontend uses "Risk" for the module Compliance lives in on the backend. Same row name on
  // both sides — only the module enum slot differs, which is reconciled in Phase 1's seed.
  ComplianceAdmin:  'riskPermissions',
};

// Single source of truth for two related questions:
//   1. Legacy: "is the current user a CorporateAdmin / PermissionsAdmin / SuperAdmin?"
//      Backed by the per-company per-module Permission table (string flags). Replaces the
//      hardcoded `[3, 37, 14, 25, 40].includes(userId)` checks that used to live inline in
//      eight components and silently went wrong on the original incident.
//   2. Role-based: "can the current user perform <action> on <pageKey>?"
//      Backed by the new RolePermission table via GET /effective-permissions, returned as a
//      flat { pageKey -> actionBitmask } map per (user, company). Use can() / can$().
//
// The legacy block exists during cutover. Once every route guard / *ngIf has migrated to the
// can() path, the corporatePermissions cache and isCorporateAdmin helpers can be deleted.
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  static readonly CORPORATE_ADMIN = 'CorporateAdmin';
  static readonly PERMISSIONS_ADMIN = 'PermissionsAdmin';
  static readonly SUPER_ADMIN = 'SuperAdmin';

  private readonly userService = inject(UserService);
  private readonly companiesService = inject(CompaniesService);
  private readonly rolesService = inject(RolesService);

  private readonly corporatePermissions$ = new BehaviorSubject<string[] | null>(null);
  // Cache of the flat effective-permissions map for the currently-selected company. Null
  // means "not loaded yet" (the can() fast path then returns false — safer default than
  // optimistic true). The localStorage cache is keyed by companyId so a tab switch picks up
  // the right slice without an extra HTTP call when the cache is warm.
  private readonly effectivePermissions$ = new BehaviorSubject<Record<string, number> | null>(null);

  private get companyId(): number {
    return this.companiesService.managedCompanyId;
  }

  private effectivePermissionsCacheKey(companyId: number): string {
    return `effectivePermissions:${companyId}`;
  }

  // ─── Legacy: per-module flag checks ──────────────────────────────────────────────────

  // Synchronous fast path. switchCompanyAndNavigate already populates `corporatePermissions`
  // in localStorage on company switch and after sign-in. Used by code that can't await an
  // HTTP call (template *ngIf, list filters). Returns false when the cache hasn't been
  // populated yet — that path is intentional: a non-admin is the safer default if perms are
  // unknown.
  isCorporateAdmin(): boolean {
    return this.readCachedCorporatePermissions().includes(PermissionsService.CORPORATE_ADMIN);
  }

  // Observable version. Replays the cached value immediately, then refreshes from the server
  // in the background so any drift between localStorage and the API is corrected within one
  // tick. Most callers want this — it survives company switches and never goes stale.
  isCorporateAdmin$(): Observable<boolean> {
    return this.flagsStream().pipe(
      map(perms => perms.includes(PermissionsService.CORPORATE_ADMIN)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  isPermissionsAdmin(): boolean {
    const perms = this.readCachedCorporatePermissions();
    return perms.includes(PermissionsService.PERMISSIONS_ADMIN)
        || perms.includes(PermissionsService.SUPER_ADMIN);
  }

  isPermissionsAdmin$(): Observable<boolean> {
    return this.flagsStream().pipe(
      map(perms =>
        perms.includes(PermissionsService.PERMISSIONS_ADMIN) ||
        perms.includes(PermissionsService.SUPER_ADMIN)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  isSuperAdmin(): boolean {
    return this.readCachedCorporatePermissions().includes(PermissionsService.SUPER_ADMIN);
  }

  isSuperAdmin$(): Observable<boolean> {
    return this.flagsStream().pipe(
      map(perms => perms.includes(PermissionsService.SUPER_ADMIN)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  // Synchronous "does the user hold the given legacy flag" check. Used by the Phase 6
  // pagePermissionGuard as a cutover fallback — admins who haven't been migrated to roles
  // yet still pass through the same routes they did before. Reads straight from localStorage
  // for the same fast-path reason as isCorporateAdmin(): a guard can't await.
  hasLegacyFlag(flag: LegacyPermission): boolean {
    const key = LEGACY_CACHE_KEY[flag];
    if (!key) return false;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.includes(flag);
    } catch {
      return false;
    }
  }

  // Force a fresh permission read. Called after operations that may change permissions or
  // when a component wants to bypass the cache. Updates both localStorage and the in-memory
  // subject.
  refreshCorporatePermissions(): Observable<string[]> {
    return this.userService.getModulePermissionsById(this.companyId, Modules.Corporate).pipe(
      tap(perms => {
        try { localStorage.setItem('corporatePermissions', JSON.stringify(perms)); }
        catch { /* private browsing or quota — surfacing this would be noise */ }
        this.corporatePermissions$.next(perms);
      })
    );
  }

  // ─── Role-based: page+action checks ──────────────────────────────────────────────────

  // Synchronous fast path for templates and list filters. Reads the in-memory cache (falling
  // back to localStorage if the subject is empty — covers the first-render-after-reload case
  // before refreshEffectivePermissions has resolved). Returns false when the cache is empty
  // for the same defensive-default reason as isCorporateAdmin: an unknown permission is a
  // denied permission.
  can(pageKey: string, action: PermissionAction = PermissionAction.View): boolean {
    const map = this.effectivePermissions$.value ?? this.readCachedEffectivePermissions();
    const mask = map?.[pageKey] ?? 0;
    return hasAction(mask, action);
  }

  // Reactive version used by *hasPermission and route guards. Lazily loads the effective
  // permissions for the current company if the subject is null, so callers that mount
  // before bootstrap finishes still resolve correctly. The shareReplay/refCount pair keeps
  // the HTTP fan-out at one request per emitting subject.
  can$(pageKey: string, action: PermissionAction = PermissionAction.View): Observable<boolean> {
    return this.effectivePermissionsStream().pipe(
      map(perms => hasAction(perms[pageKey] ?? 0, action)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  // Bootstrap path: called from home.component and header.component after sign-in / company
  // switch. Takes userId + companyId explicitly because PermissionsService doesn't own the
  // "who am I" question — UserService and CompaniesService do, and threading them in here
  // would create a circular dep with UserService (which already imports CompaniesService).
  refreshEffectivePermissions(userId: number, companyId: number): Observable<EffectivePermissions> {
    return this.rolesService.getEffectivePermissions(userId, companyId).pipe(
      tap(result => {
        try {
          localStorage.setItem(
            this.effectivePermissionsCacheKey(companyId),
            JSON.stringify(result.permissions)
          );
        } catch { /* quota / private mode — non-fatal */ }
        this.effectivePermissions$.next(result.permissions);
      })
    );
  }

  // Called when an admin edits a role the current user holds, so the UI updates without a
  // full reload. Sync with the current company.
  invalidateEffectivePermissions(): void {
    this.effectivePermissions$.next(null);
    try {
      localStorage.removeItem(this.effectivePermissionsCacheKey(this.companyId));
    } catch { /* non-fatal */ }
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────────────

  private flagsStream(): Observable<string[]> {
    const cached = this.readCachedCorporatePermissions();
    this.corporatePermissions$.next(cached);
    return this.corporatePermissions$.pipe(
      switchMap(value => {
        if (value !== null) return of(value);
        return this.refreshCorporatePermissions();
      })
    );
  }

  private effectivePermissionsStream(): Observable<Record<string, number>> {
    const cached = this.readCachedEffectivePermissions();
    if (cached && this.effectivePermissions$.value === null) {
      this.effectivePermissions$.next(cached);
    }
    return this.effectivePermissions$.pipe(
      switchMap(value => {
        if (value !== null) return of(value);
        // No userId in scope here, so kick the bootstrap path: read the current user, then
        // pull effective perms for the active company. Cached behind shareReplay above.
        return this.userService.getCurrentUser(false).pipe(
          switchMap(u => this.refreshEffectivePermissions(u.userId, this.companyId)),
          map(r => r.permissions)
        );
      })
    );
  }

  private readCachedCorporatePermissions(): string[] {
    try {
      const raw = localStorage.getItem('corporatePermissions');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private readCachedEffectivePermissions(): Record<string, number> | null {
    try {
      const raw = localStorage.getItem(this.effectivePermissionsCacheKey(this.companyId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
    } catch {
      return null;
    }
  }
}
